import React, { useState, useEffect } from "react";
import { Box, Typography, Grid } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import { Calculator } from "lucide-react";
import { EstimatingSkeleton } from "../skeletons";
import { useProject, useNotification } from "../../../../hooks";
import { useInspectionAttributes } from "../../../../hooks/query";

import SiteSetup from "./SiteSetup";
import ComplexityAdjustments from "./ComplexityAdjustments";
import PaymentConfig from "./PaymentConfig";
import FeesAndIncidentals from "./FeesAndIncidentals";

interface EstimatingProps {
    measurements: any[];
}

const DEFAULT_ESTIMATING_VALUES = {
    EST_PAYMENT_TYPE: "insurance",
    EST_WASTE_FACTOR: 10,
    EST_STEEP_PITCH: false,
    EST_STEEP_TYPE: "7-9",
    EST_TEAR_OFF_LAYERS: "1",
    EST_STORIES: "1",
    EST_DEDUCTIBLE: "",
    EST_OP_PERCENT: "20",
    EST_PERMIT_COST: "150.00",
    EST_DUMPSTER_FEE: "450.00",
    EST_EAGLEVIEW_COST: "75.00",
    EST_PLYWOOD_COST: "85.00",
    EST_FASCIA_COST: "12.50",
};

export default function Estimating({ measurements }: EstimatingProps) {
    const { project } = useProject();
    const { showError } = useNotification();
    const { useListByProjectId, useUpdateSingleAttribute } = useInspectionAttributes();
    const listAttributesSvc = useListByProjectId();
    const updateAttributeSvc = useUpdateSingleAttribute();

    const [isLoading, setIsLoading] = useState(true);

    const [values, setValues] = useState<Record<string, any>>(DEFAULT_ESTIMATING_VALUES);

    useEffect(() => {
        const fetchAttributes = async () => {
            if (!project?.id) return;
            try {
                const data = await listAttributesSvc.mutateAsync({ ProjectId: project.id });

                const dbValues: Record<string, any> = {};
                
                data.forEach((attr: any) => {
                    if (attr.AttributeName.startsWith("EST_")) {
                        let val = attr.AttributeValue;
                        if (attr.AttributeName === "EST_WASTE_FACTOR") val = Number(val);
                        if (attr.AttributeName === "EST_STEEP_PITCH") val = val === "true";
                        dbValues[attr.AttributeName] = val;
                    }
                });

                data.forEach((attr: any) => {
                    const val = attr.AttributeValue;
                    if (!val) return;

                    if (attr.AttributeName === "Roof Pitch" && dbValues.EST_STEEP_PITCH === undefined) {
                        const pitchMatch = val.match(/(\d+)\/12/);
                        if (pitchMatch) {
                            const pitchNum = parseInt(pitchMatch[1]);
                            if (pitchNum >= 7) {
                                dbValues.EST_STEEP_PITCH = true;
                                dbValues.EST_STEEP_TYPE = pitchNum <= 9 ? "7-9" : pitchNum <= 12 ? "10-12" : "13+";
                            }
                        }
                    }

                    if (attr.AttributeName === "Tear Off Layer" && !dbValues.EST_TEAR_OFF_LAYERS) {
                        if (val.toLowerCase().includes("2nd") || val.includes("2")) dbValues.EST_TEAR_OFF_LAYERS = "2";
                        else if (val.toLowerCase().includes("3rd") || val.includes("3")) dbValues.EST_TEAR_OFF_LAYERS = "3";
                        else dbValues.EST_TEAR_OFF_LAYERS = "1";
                    }
                });

                setValues((prev) => {
                    const next = { ...prev, ...dbValues };
                    console.log("Estimating Smart Hydrated Values:", next);
                    return next;
                });
                setIsLoading(false);
            } catch (error) {
                console.error("Error fetching estimating attributes:", error);
                setIsLoading(false);
            }
        };

        fetchAttributes();
    }, [project?.id]);

    const handleUpdate = async (key: string, value: any) => {
        if (!project?.id) return;

        const oldValue = values[key];
        setValues((prev) => ({ ...prev, [key]: value }));

        try {
            await updateAttributeSvc.mutateAsync({
                ProjectId: project.id,
                AttributeName: key,
                Value: String(value),
            });
        } catch (error) {
            console.error("Error updating attribute:", error);
            showError(`Failed to save ${key.replace("EST_", "").replace(/_/g, " ").toLowerCase()}`);
            setValues((prev) => ({ ...prev, [key]: oldValue }));
        }
    };

    return (
        <AnimatePresence mode="popLayout">
            {isLoading ? (
                <motion.div
                    key="skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <EstimatingSkeleton />
                </motion.div>
            ) : (
                <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Box>
                        <Box mb={2}>
                            <Typography level="title-md" startDecorator={<Calculator size={16} />}>
                                Estimating Configuration
                            </Typography>
                            <Typography level="body-sm" color="neutral">
                                Global billing rules, contingencies, and logistical fees.
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            {/* ── Column 1: Site Setup & Structure ── */}
                            <Grid xs={12} md={7}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <SiteSetup values={values} onChange={handleUpdate} />
                                    <ComplexityAdjustments values={values} onChange={handleUpdate} />
                                </Box>
                            </Grid>

                            {/* ── Column 2: Financials & Logistics ── */}
                            <Grid xs={12} md={5}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <PaymentConfig values={values} onChange={handleUpdate} />
                                    <FeesAndIncidentals values={values} onChange={handleUpdate} />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
